import React, { useState, useEffect } from 'react';
import { ModelProvider } from './context/ModelContext';
import { MainLayout } from './components/MainLayout';
import ResearchWorkspace from './components/ResearchWorkspace';
import ResearchChatDock from './components/ResearchChatDock';
import { initBackendUrl, getBackendUrlSync, invalidateBackendUrl } from './services/backendUrl';
import { apiService } from './services/api';

// ─── Backend URL settings modal ──────────────────────────────────────────────

const LS_KEY = 'trade_model_backend_url';

const BackendUrlModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [value, setValue] = useState(() => localStorage.getItem(LS_KEY) || '');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    const trimmed = value.trim();
    if (trimmed) {
      localStorage.setItem(LS_KEY, trimmed);
    } else {
      localStorage.removeItem(LS_KEY);
    }
    invalidateBackendUrl();
    // Re-initialise so subsequent requests use the new URL
    initBackendUrl().then(() => apiService.init());
    setSaved(true);
    setTimeout(onClose, 800);
  };

  const handleClear = () => {
    setValue('');
    localStorage.removeItem(LS_KEY);
    invalidateBackendUrl();
    initBackendUrl().then(() => apiService.init());
    setSaved(true);
    setTimeout(onClose, 800);
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.box} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <span style={modalStyles.title}>Backend URL Override</span>
          <button style={modalStyles.closeBtn} onClick={onClose}>x</button>
        </div>
        <p style={modalStyles.desc}>
          Manually set the backend URL (e.g.{' '}
          <code>https://app.taiji-furusawa-econ.com</code>). Leave blank to auto-discover from Gist.
        </p>
        <input
          style={modalStyles.input}
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value); setSaved(false); }}
          placeholder="https://app.taiji-furusawa-econ.com"
          spellCheck={false}
        />
        <div style={modalStyles.current}>
          Currently resolved: <code>{getBackendUrlSync()}</code>
        </div>
        <div style={modalStyles.actions}>
          <button style={modalStyles.clearBtn} onClick={handleClear}>Clear override</button>
          <button style={modalStyles.saveBtn} onClick={handleSave}>
            {saved ? 'Saved!' : 'Save & reload'}
          </button>
        </div>
      </div>
    </div>
  );
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  box: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    padding: '24px',
    maxWidth: '480px',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  title: {
    fontWeight: 600,
    fontSize: '1rem',
    color: 'var(--color-text-strong)',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.1rem',
    cursor: 'pointer',
    color: 'var(--color-text-subtle)',
    padding: '4px 8px',
  },
  desc: {
    fontSize: '0.85rem',
    color: 'var(--color-text-subtle)',
    marginBottom: '12px',
    lineHeight: 1.5,
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
    color: 'var(--color-text)',
    backgroundColor: 'var(--color-bg)',
    boxSizing: 'border-box',
  },
  current: {
    marginTop: '8px',
    fontSize: '0.78rem',
    color: 'var(--color-text-subtle)',
  },
  actions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  clearBtn: {
    padding: '7px 14px',
    background: 'none',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: 'var(--color-text-subtle)',
  },
  saveBtn: {
    padding: '7px 14px',
    backgroundColor: 'var(--color-primary)',
    color: '#fff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
};

// ─── Root ─────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  useEffect(() => {
    void initBackendUrl().then(() => apiService.init()).catch(() => {});
  }, []);

  return (
    <ModelProvider>
      <AppContent />
    </ModelProvider>
  );
};

// The full-UI experience: model selector + dashboard.
const AppContent: React.FC = () => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="app-root">
      <div className="app-main">
        <MainLayout
          onOpenSettings={() => setSettingsOpen(true)}
        >
          <ResearchWorkspace />
        </MainLayout>
      </div>

      {settingsOpen && (
        <BackendUrlModal onClose={() => setSettingsOpen(false)} />
      )}
      <ResearchChatDock />
    </div>
  );
};

export default App;
